// One texture per live source, uploaded only while some consumer wants it (sources-v2 D4).
import { CanvasSource, Texture } from 'pixi.js'
import { HEIGHT, IS_DEV, WIDTH } from '~/lib/constants'

const DESTROY_DELAY_MS = 1000

const entries = new Map()
const wants = new Map()
const frameListeners = new Set()

const entry = id => {
	let e = entries.get(id)
	if (!e) {
		const canvas = new OffscreenCanvas(WIDTH, HEIGHT)
		const source = new CanvasSource({ resource: canvas })
		// `dynamic`: the first frame resizes the source off stage size, and a Sprite otherwise keeps the quad it baked at construction.
		const texture = new Texture({ source, dynamic: true })
		e = {
			id,
			canvas,
			source,
			texture,
			users: 0,
			frames: 0,
			width: 0,
			height: 0,
			reset: true,
			renderer: null,
			timer: 0,
		}
		entries.set(id, e)
	}
	return e
}

// ⚠️ Deferred, and never mid-fade: a fade's ghost may still draw a texture its sprite released.
const destroyLater = e => {
	clearTimeout(e.timer)
	e.timer = setTimeout(() => {
		if (e.users > 0 || entries.get(e.id) !== e) return
		if (window.pixi_transition_active) return destroyLater(e)
		entries.delete(e.id)
		e.texture.destroy()
		e.source.destroy()
	}, DESTROY_DELAY_MS)
}

/** Takes a ref on a source's texture, creating it. */
export const retainSource = id => {
	const e = entry(id)
	e.users++
	clearTimeout(e.timer)
	return e.texture
}

export const releaseSource = id => {
	const e = entries.get(id)
	if (!e) return
	e.users = Math.max(0, e.users - 1)
	if (!e.users) destroyLater(e)
}

/** A consumer's one wanted source (null: none). Keeps its texture fed even before anything draws it — how a switch gets the first frame it waits for. */
export const wantSource = (consumer, id) => {
	const prev = wants.get(consumer) ?? null
	if (prev === id) return
	if (id) {
		wants.set(consumer, id)
		retainSource(id)
	} else wants.delete(consumer)
	if (prev) releaseSource(prev)
}

const holds = new Map()
const holdListeners = new Set()

/** Keeps a source fed, and through `onHold` its producer open, until the returned release runs: a fade's outgoing side (sources-v2 D5). */
export const holdSource = id => {
	const consumer = Symbol(id)
	wantSource(consumer, id)
	holds.set(id, (holds.get(id) ?? 0) + 1)
	for (const fn of holdListeners) fn(id, true, consumer)
	let held = true
	return () => {
		if (!held) return
		held = false
		const n = holds.get(id) - 1
		if (n) holds.set(id, n)
		else holds.delete(id)
		wantSource(consumer, null)
		for (const fn of holdListeners) fn(id, false, consumer)
	}
}

export const isHeld = id => holds.has(id)

/** `fn(id, held, token)` on every hold and release; `token` is the same for a hold and its release. @returns {() => void} unsubscribe */
export const onHold = fn => {
	holdListeners.add(fn)
	return () => holdListeners.delete(fn)
}

export const isWanted = id => (entries.get(id)?.users ?? 0) > 0

export const hasFrame = id => (entries.get(id)?.frames ?? 0) > 0

/** The source's picture aspect (w / h) once a frame landed, else null. */
export const sourceAspect = id => {
	const e = entries.get(id)
	return e?.frames && e.width > 0 && e.height > 0 ? Math.round((e.width / e.height) * 1e4) / 1e4 : null
}

/** Fires once per source each time its texture gets a first frame. @returns {() => void} unsubscribe */
export const onFirstFrame = fn => {
	frameListeners.add(fn)
	return () => frameListeners.delete(fn)
}

/** Uploads a frame into a wanted source's texture and closes the bitmap. Snapshot `app` before any await: a PixiStage unmount destroys the renderer. */
export const uploadSourceFrame = (id, app, bitmap) => {
	const e = entries.get(id)
	if (!e?.users || e.texture.destroyed) {
		bitmap.close()
		return
	}
	if (e.renderer !== app.renderer) {
		e.renderer = app.renderer
		e.reset = true
	}
	// Zeroed glTex dimensions force a texImage2D upload; without it the video renders black when PixiJS allocated the texture first.
	if (e.reset) {
		const glTex = app.renderer.texture.getGlSource(e.source)
		if (glTex) {
			glTex.width = 0
			glTex.height = 0
		}
		e.reset = false
	}
	e.width = bitmap.width
	e.height = bitmap.height
	const first = !e.frames++
	// ⚠️ HOLD THE LAST FRAME for the rig: a live feed makes a settled reference unreproducible against itself. A fresh texture still takes its first frame, or a source re-created under the freeze stays blank.
	if (!window.__freeze_video || first) {
		e.source.resource = bitmap
		e.source.update()
	}
	bitmap.close()
	if (first) for (const fn of frameListeners) fn(id)
}

/** The source's producer stopped: what the texture holds is no longer its picture, so a switch to it waits for a fresh frame. */
export const staleSource = id => {
	const e = entries.get(id)
	if (!e?.frames) return
	e.frames = 0
	e.reset = true
}

/** Blanks every source texture to black — the pipeline stopped, so no picture is current. */
export const clearSourceTextures = () => {
	for (const e of entries.values()) {
		if (e.texture.destroyed) continue
		// A feeder leaves an ImageBitmap as the resource; the canvas must be the resource again before drawing into it.
		e.source.resource = e.canvas
		const ctx = e.canvas.getContext('2d')
		ctx.fillStyle = '#000000'
		ctx.fillRect(0, 0, WIDTH, HEIGHT)
		e.source.update()
		e.frames = 0
		e.reset = true
	}
}

if (IS_DEV) {
	window.textures = {
		snapshot: () =>
			Object.fromEntries(
				[...entries.values()].map(e => [
					e.id,
					{ users: e.users, frames: e.frames, size: `${e.width}×${e.height}` },
				])
			),
		wants: () =>
			Object.fromEntries([...wants].map(([c, id]) => [typeof c === 'symbol' ? `hold:${c.description}` : c, id])),
		holds: () => Object.fromEntries(holds),
		source: id => entries.get(id)?.source ?? null,
	}
}
